import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuro, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Link } from "react-router-dom";
import { Download, Upload, Plus, Trash2, FileSpreadsheet } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Invoices() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [year, setYear] = useState(2026);
  const [form, setForm] = useState({
    kind: "expense", date: todayStr(), party: "", doc_no: "", description: "",
    account_code: "6500", cost_center: "TAF", net: 0, vat: 0, nights: 0,
  });

  const { data: meta } = useQuery({ queryKey: ["finance-accounts"], queryFn: async () => (await api.get("/finance/accounts")).data });
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["entries", year, "expense"],
    queryFn: async () => (await api.get(`/finance/entries?year=${year}&kind=expense`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["entries"] });
    qc.invalidateQueries({ queryKey: ["pl-report"] });
    qc.invalidateQueries({ queryKey: ["pl-kpi"] });
  };

  const create = useMutation({
    mutationFn: async () => (await api.post("/finance/entries", {
      ...form, net: Number(form.net) || 0, vat: Number(form.vat) || 0,
      nights: Number(form.nights) || 0,
    })).data,
    onSuccess: () => { refresh(); toast.success("Η καταχώρηση αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/finance/entries/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const inp = "mt-1 w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";
  const lab = "text-xs uppercase tracking-wider text-[#6F6A60] font-semibold";
  const totals = rows.reduce((a, r) => ({
    expense: a.expense + r.net,
    vat: a.vat + (r.vat || 0),
    count: a.count + 1,
  }), { expense: 0, vat: 0, count: 0 });

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Έξοδα & Παραστατικά</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Τιμολόγια Προμηθευτών</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Μόνο έξοδα εδώ — τα έσοδα (κρατήσεις & εκδηλώσεις) βρίσκονται στη σελίδα «Έσοδα & Εκδηλώσεις».</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <button data-testid="download-template" onClick={() => downloadXlsx("/finance/template.xlsx", "TAF_Template_Parastatika.xlsx")}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#6E7A63] hover:border-[#6E7A63]">
            <Download className="h-4 w-4" /> Πρότυπο Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[["Έξοδα (καθαρά)", totals.expense], ["ΦΠΑ", totals.vat],
          ["Παραστατικά", totals.count]].map(([l, v], i) => (
          <div key={l} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`inv-kpi-${l}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{l}</div>
            <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{i === 2 ? v : fmtEuro(v)}</div>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="inv-import-hint">
          <div>
            <h2 className="font-medium text-[#45423C] flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-[#C86235]" /> Μαζική εισαγωγή εξόδων από Excel
            </h2>
            <p className="text-sm text-[#6F6A60] mt-1">Τα έξοδα/παραστατικά έχουν πλέον δική τους καρτέλα στο Κέντρο Εισαγωγών, ξεχωριστά από τις κρατήσεις.</p>
          </div>
          <Link to="/imports" data-testid="inv-goto-imports"
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A]">
            <Upload className="h-4 w-4" /> Εισαγωγές Excel
          </Link>
        </div>
      )}

      {canEdit && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="entry-form">
          <h2 className="font-medium text-[#45423C] mb-4">Νέο έξοδο / τιμολόγιο προμηθευτή</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div>
              <label className={lab}>Ημερομηνία</label>
              <input data-testid="entry-date" type="date" className={inp} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className={lab}>Προμηθευτής / Πελάτης</label>
              <input data-testid="entry-party" className={inp} value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Αρ. Παραστατικού</label>
              <input data-testid="entry-doc" className={inp} value={form.doc_no} onChange={(e) => setForm({ ...form, doc_no: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Cost Center</label>
              <select data-testid="entry-cc" className={inp} value={form.cost_center} onChange={(e) => setForm({ ...form, cost_center: e.target.value })}>
                {(meta?.cost_centers || []).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={lab}>Κατηγορία (USALI)</label>
              <select data-testid="entry-account" className={inp} value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })}>
                {(meta?.accounts || []).filter((a) => (form.kind === "income" ? a.code.startsWith("4") : /^[5-7]/.test(a.code))).map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={lab}>Περιγραφή</label>
              <input data-testid="entry-desc" className={inp} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Καθαρή Αξία (€)</label>
              <input data-testid="entry-net" type="number" className={inp} value={form.net} onChange={(e) => setForm({ ...form, net: e.target.value })} />
            </div>
            <div>
              <label className={lab}>ΦΠΑ (€)</label>
              <input data-testid="entry-vat" type="number" className={inp} value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} />
            </div>
            {form.account_code === "4100" && (
              <div>
                <label className={lab}>Διανυκτερεύσεις</label>
                <input data-testid="entry-nights" type="number" className={inp} value={form.nights} onChange={(e) => setForm({ ...form, nights: e.target.value })} />
              </div>
            )}
            <div className="flex items-end">
              <button data-testid="entry-save" onClick={() => create.mutate()} disabled={create.isPending}
                className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
                <Plus className="h-4 w-4" /> Καταχώρηση
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-medium text-[#45423C]">Έξοδα {year}</h2>
          <div className="flex gap-2">
            <select data-testid="inv-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none">
              {[2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3">Ημ/νία</th>
                <th className="px-4 py-3">Προμηθευτής</th>
                <th className="px-4 py-3">Παραστατικό</th>
                <th className="px-4 py-3">Κατηγορία</th>
                <th className="px-4 py-3">CC</th>
                <th className="px-4 py-3 text-right">Καθαρή</th>
                <th className="px-4 py-3 text-right">ΦΠΑ</th>
                <th className="px-4 py-3 text-right">Σύνολο</th>
                {canEdit && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`entry-row-${r.id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2 tabular">{fmtDate(r.date)}</td>
                  <td className="px-4 py-2 font-medium text-[#45423C]">{r.party || "—"}</td>
                  <td className="px-4 py-2 text-[#6F6A60]">{r.doc_no || "—"}</td>
                  <td className="px-4 py-2 text-[#6F6A60]">{r.account_code} · {r.account_name}</td>
                  <td className="px-4 py-2">{r.cost_center}</td>
                  <td className="px-4 py-2 text-right tabular">{fmtEuro(r.net)}</td>
                  <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{fmtEuro(r.vat)}</td>
                  <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(r.total)}</td>
                  {canEdit && (
                    <td className="px-4 py-2 text-right">
                      <button data-testid={`entry-delete-${r.id}`} onClick={() => remove.mutate(r.id)} className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">Κανένα έξοδο — κατεβάστε το πρότυπο Excel ή καταχωρήστε τιμολόγιο εδώ.</td></tr>
              )}
            </tbody>
            {!isLoading && rows.length > 0 && (
              <tfoot>
                <tr className="bg-[#45423C] text-white font-semibold" data-testid="invoices-totals">
                  <td className="px-4 py-2.5" colSpan={5}>ΣΥΝΟΛΟ {rows.length} παραστατικών</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(totals.expense)}</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(totals.vat)}</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(totals.expense + totals.vat)}</td>
                  {canEdit && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
